import type { FunctionComponent, PropsWithChildren } from 'react';
import { Link, LinkProps } from '@property.com.au/ui-kit';
import { trackOutboundLinkClick } from '../../../analytics/event-tracking/trackOutboundLinkClick';

export const setRel = (click_url: string): string => {
  let isInternalUrl: boolean;
  const whiteListedHostnameEnding = ['realestate.com.au', 'property.com.au', 'rea-group.com'];

  try {
    const { hostname } = new URL(click_url);
    isInternalUrl = whiteListedHostnameEnding.some((hostnameEnding) => {
      return hostname.endsWith(hostnameEnding);
    });
  } catch {
    isInternalUrl = false;
  }

  if (click_url.startsWith('/') || isInternalUrl) return 'noopener';
  if (click_url.includes('careforkids.com.au')) return 'noopener noreferrer';
  return 'noopener noreferrer nofollow';
};

interface OutboundLinkTracking {
  schema?: string;
  data: {
    event_name?: string;
    type: string;
    click_url: string;
  };
  rel?: string;
}

export const outboundLinkAttributeHelper = ({
  schema,
  data: { event_name, type, click_url },
  rel = undefined,
}: OutboundLinkTracking) => {
  return {
    target: '_blank',
    rel: rel || setRel(click_url),
    onClick: () =>
      trackOutboundLinkClick({ url: click_url, outboundLinkType: type, schema, event_name }),
  };
};

export interface OutboundLinkProps extends LinkProps {
  href: string;
  outboundLinkType: string;
  title?: string;
  schema?: string;
  event_name?: string;
  rel?: string;
  target?: string;
  onClick?: () => void;
}

export const OutboundLink: FunctionComponent<PropsWithChildren<OutboundLinkProps>> = ({
  href,
  outboundLinkType,
  schema,
  event_name,
  rel,
  target,
  onClick,
  ...rest
}) => {
  const {
    target: outboundLinkTarget,
    rel: outboundLinkRel,
    onClick: outboundLinkOnClick,
  } = outboundLinkAttributeHelper({
    schema,
    data: { click_url: href, type: outboundLinkType, event_name },
    rel,
  });

  return (
    <Link
      href={href}
      target={target ?? outboundLinkTarget}
      rel={rel ?? outboundLinkRel}
      onClick={() => {
        outboundLinkOnClick();
        onClick?.();
      }}
      {...rest}
    />
  );
};
